import React from "react";
import { MapStyleId } from "../../types";
import { getMapStyleInfo } from "../../config/mapStyles";
import { MapStyleButton, MapStyleOption } from "../common/MapStyleButton";

interface MapStylesDetailViewProps {
  mapPreferences: {
    defaultStyleId: string;
    starredStyleIds: string[];
    overlayOpacity?: number;
  };
  mapStyles: MapStyleOption[];
  onSelectMapStyle: (id: MapStyleId) => void;
  onToggleStar: (id: MapStyleId) => void;
  onUpdateOverlay: (opacity: number) => void;
  onSettingsClick: () => void;
  onClose: () => void;
}

export const MapStylesDetailView: React.FC<MapStylesDetailViewProps> = ({
  mapPreferences,
  mapStyles,
  onSelectMapStyle,
  onToggleStar,
  onUpdateOverlay,
  onSettingsClick,
  onClose,
}) => {
  const starredStyles = mapStyles.filter((style) =>
    mapPreferences.starredStyleIds.includes(style.id),
  );
  const openMaps = mapStyles.filter(
    (style) => !style.id.startsWith("thunderforest:"),
  );
  const thunderforestMaps = mapStyles.filter((style) =>
    style.id.startsWith("thunderforest:"),
  );

  return (
    <>
      <div
        className="island-header"
        style={{ marginBottom: "4px", padding: "8px 8px 4px 8px" }}
      >
        <div className="island-header-content">
          <div className="island-icon">🗺️</div>
          <div className="island-text">
            <div className="island-title">Map Styles</div>
            <div className="island-subtitle">
              Current:{" "}
              {getMapStyleInfo(mapPreferences.defaultStyleId)?.name ||
                "Standard"}
            </div>
          </div>
        </div>
        <div
          className="island-actions"
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          {/* Satellite overlay slider in header */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "4px",
              marginRight: "8px",
            }}
          >
            <span style={{ fontSize: "12px" }}>🛰️</span>
            <input
              type="range"
              min="0"
              max="80"
              value={(() => {
                const opacity = mapPreferences.overlayOpacity || 0;
                if (opacity === 0) return 0;
                return Math.round(opacity * 100);
              })()}
              onChange={(e) => {
                let value = parseInt(e.target.value);
                let actualValue: number;
                if (value === 0) {
                  actualValue = 0;
                } else {
                  let snapped = Math.round(value / 10) * 10;
                  if (snapped === 0) snapped = 10;
                  actualValue = snapped / 100;
                }
                onUpdateOverlay(actualValue);
              }}
              style={{
                width: "60px",
                height: "3px",
                background: "rgba(255,255,255,0.2)",
                outline: "none",
                WebkitAppearance: "none",
                borderRadius: "2px",
              }}
            />
            <span
              style={{
                fontSize: "10px",
                color: "var(--text-primary)",
                minWidth: "28px",
                textAlign: "right",
                fontWeight: mapPreferences.overlayOpacity === 0 ? 600 : 400,
              }}
            >
              {mapPreferences.overlayOpacity === 0
                ? "OFF"
                : `${Math.round((mapPreferences.overlayOpacity || 0) * 100)}%`}
            </span>
          </div>
          <button
            className="island-btn default"
            onClick={onSettingsClick}
            style={{
              fontSize: "10px",
              padding: "4px 8px",
              minWidth: "60px",
            }}
          >
            Settings
          </button>
          <button className="island-close" onClick={onClose}>
            ×
          </button>
        </div>
      </div>

      <div className="island-details" style={{ padding: "8px" }}>
        {/* Map styles grid */}
        <div
          style={{
            maxHeight: "140px",
            overflowY: "auto",
            paddingRight: "4px",
          }}
        >
          {/* Starred section */}
          {starredStyles.length > 0 && (
            <>
              <div
                style={{
                  fontSize: "9px",
                  fontWeight: 600,
                  color: "var(--text-secondary)",
                  marginBottom: "4px",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                <span>⭐</span> STARRED
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(4, 1fr)",
                  gap: "6px",
                  marginBottom: "8px",
                }}
              >
                {starredStyles.map((style) => (
                  <MapStyleButton
                    key={style.id}
                    style={style}
                    isSelected={mapPreferences.defaultStyleId === style.id}
                    isStarred={mapPreferences.starredStyleIds.includes(
                      style.id,
                    )}
                    onSelect={onSelectMapStyle}
                    onToggleStar={onToggleStar}
                    enableLongPress={true}
                    variant="compact"
                  />
                ))}
              </div>
            </>
          )}

          {/* Open Maps section */}
          <div
            style={{
              fontSize: "9px",
              fontWeight: 600,
              color: "var(--text-secondary)",
              marginBottom: "4px",
              marginTop: starredStyles.length > 0 ? "8px" : "0",
              display: "flex",
              alignItems: "center",
              gap: "4px",
            }}
          >
            <span>🆓</span> OPEN MAPS
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(4, 1fr)",
              gap: "6px",
              marginBottom: "8px",
            }}
          >
            {openMaps.map((style) => (
              <MapStyleButton
                key={style.id}
                style={style}
                isSelected={mapPreferences.defaultStyleId === style.id}
                isStarred={mapPreferences.starredStyleIds.includes(style.id)}
                onSelect={onSelectMapStyle}
                onToggleStar={onToggleStar}
                enableLongPress={true}
                variant="compact"
              />
            ))}
          </div>

          {/* Thunderforest Maps section */}
          {thunderforestMaps.length > 0 && (
            <>
              <div
                style={{
                  fontSize: "9px",
                  fontWeight: 600,
                  color: "var(--text-secondary)",
                  marginBottom: "4px",
                  marginTop: "8px",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                <span>🗺️</span> THUNDERFOREST
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(4, 1fr)",
                  gap: "6px",
                }}
              >
                {thunderforestMaps.map((style) => (
                  <MapStyleButton
                    key={style.id}
                    style={style}
                    isSelected={mapPreferences.defaultStyleId === style.id}
                    isStarred={mapPreferences.starredStyleIds.includes(
                      style.id,
                    )}
                    onSelect={onSelectMapStyle}
                    onToggleStar={onToggleStar}
                    enableLongPress={true}
                    variant="compact"
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};
